import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, AlertTriangle, Stethoscope, ArrowRight } from 'lucide-react';
import { symptomsList } from '../data/mockData';
export function SymptomCheckerScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const toggleSymptom = (symptom: string) => {
    if (selectedSymptoms.includes(symptom)) {
      setSelectedSymptoms(selectedSymptoms.filter(s => s !== symptom));
    } else {
      setSelectedSymptoms([...selectedSymptoms, symptom]);
    }
  };
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <div className="animate-fade-in" data-id="element-1696">
            <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1697">
              What are your symptoms?
            </h2>
            <p className="text-text-secondary mb-6" data-id="element-1698">
              Select all that apply to help us guide you.
            </p>

            <div className="flex flex-wrap gap-3" data-id="element-1699">
              {symptomsList.map(symptom => {
              const isSelected = selectedSymptoms.includes(symptom);
              return <button key={symptom} onClick={() => toggleSymptom(symptom)} className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all flex items-center ${isSelected ? 'bg-brand-primary border-brand-primary text-white shadow-md' : 'bg-white border-ui-border text-text-main hover:border-brand-primary/50'}`} data-id="element-1700">
                    {isSelected && <Check size={16} className="mr-2" data-id="element-1701" />}
                    {symptom}
                  </button>;
            })}
            </div>
          </div>;
      case 2:
        return <div className="animate-fade-in" data-id="element-1702">
            <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1703">
              How long have you felt this way?
            </h2>
            <div className="space-y-3 mt-6" data-id="element-1704">
              {['Just started today', 'A few days', 'About a week', 'More than a week'].map(duration => <button key={duration} onClick={() => setStep(3)} className="w-full p-4 text-left bg-white border border-ui-border rounded-xl font-medium active:bg-brand-light active:border-brand-primary transition-all" data-id="element-1705">
                  {duration}
                </button>)}
            </div>
          </div>;
      case 3:
        return <div className="animate-fade-in" data-id="element-1706">
            <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1707">
              Any red flags?
            </h2>
            <p className="text-text-secondary mb-6" data-id="element-1708">
              Do you have any of these severe symptoms?
            </p>
            <div className="space-y-3" data-id="element-1709">
              {['Difficulty breathing', 'Severe chest pain', 'Loss of consciousness', 'None of the above'].map(flag => <button key={flag} onClick={() => setStep(4)} className={`w-full p-4 text-left border rounded-xl font-medium transition-all ${flag === 'None of the above' ? 'bg-white border-ui-border' : 'bg-status-emergency/5 border-status-emergency/20 text-status-emergency'}`} data-id="element-1710">
                  {flag}
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="animate-fade-in flex flex-col items-center text-center pt-8" data-id="element-1711">
            <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mb-6" data-id="element-1712">
              <Stethoscope size={40} className="text-brand-primary" data-id="element-1713" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1714">
              Recommendation
            </h2>
            <div className="bg-status-warning/10 text-status-warning px-4 py-1.5 rounded-full text-sm font-bold mb-6" data-id="element-1715">
              Moderate Urgency
            </div>
            <p className="text-text-secondary mb-8 px-4" data-id="element-1716">
              Based on your symptoms ({selectedSymptoms.join(', ')}), we
              recommend consulting with a{' '}
              <span className="font-bold text-text-main" data-id="element-1717">General Doctor</span>.
              A video consultation is suitable for your current condition.
            </p>

            <div className="w-full space-y-4" data-id="element-1718">
              <button onClick={() => navigate('/explore')} className="w-full bg-brand-primary text-white font-semibold py-4 rounded-xl shadow-md flex items-center justify-center" data-id="element-1719">
                Find a General Doctor <ArrowRight size={20} className="ml-2" data-id="element-1720" />
              </button>
              <button className="w-full bg-white border border-brand-primary text-brand-primary font-semibold py-4 rounded-xl" data-id="element-1721">
                Chat with a Nurse Now
              </button>
            </div>

            <p className="text-xs text-text-secondary mt-8 px-6" data-id="element-1722">
              Disclaimer: This tool provides general guidance and is not a
              substitute for professional medical advice, diagnosis, or
              treatment.
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-ui-bg flex flex-col" data-id="element-1723">
      <div className="pt-12 px-6 pb-4 flex items-center" data-id="element-1724">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-ui-card" data-id="element-1725">
          <ArrowLeft size={24} className="text-text-main" data-id="element-1726" />
        </button>
        <div className="flex-1 flex justify-center space-x-2 px-8" data-id="element-1727">
          {[1, 2, 3, 4].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-brand-primary' : 'bg-ui-border'}`} data-id="element-1728" />)}
        </div>
      </div>

      <div className="flex-1 px-6 pt-4" data-id="element-1729">{renderStepContent()}</div>

      {step === 1 && <div className="p-6" data-id="element-1730">
          <button onClick={() => setStep(2)} disabled={selectedSymptoms.length === 0} className="w-full bg-brand-primary text-white font-semibold py-4 rounded-xl shadow-md disabled:opacity-50 transition-all" data-id="element-1731">
            Continue
          </button>
        </div>}
    </div>;
}